import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiShield, FiCheckCircle, FiLock, FiInfo } from 'react-icons/fi';

const StyledModal = styled(Modal)`
  .modal-content {
    background-color: #020617;
    color: #f1f5f9;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 2.5rem;
    overflow: hidden;
    box-shadow: 0 50px 100px -20px rgba(0, 0, 0, 0.8);
  }
  .modal-header {
    border: none;
    padding: 3rem 3rem 1rem;
    position: relative;
    background: linear-gradient(180deg, rgba(59, 130, 246, 0.05) 0%, transparent 100%);
    
    .btn-close {
      position: absolute;
      top: 2rem;
      right: 2rem;
      filter: invert(1) grayscale(100%) brightness(200%);
      z-index: 10;
    }
  }
  .modal-body {
    padding: 1rem 3rem 3rem;
    max-height: 70vh;
    overflow-y: auto;
    scrollbar-gutter: stable;
    scrollbar-width: thin;
    scrollbar-color: rgba(96, 165, 250, 0.68) rgba(255, 255, 255, 0.025);

    &::-webkit-scrollbar {
      width: 11px;
    }

    &::-webkit-scrollbar-track {
      margin-block: 10px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.025);
    }

    &::-webkit-scrollbar-thumb {
      min-height: 64px;
      border: 3px solid #020617;
      border-radius: 999px;
      background: linear-gradient(
        180deg,
        rgba(125, 190, 255, 0.88),
        rgba(59, 130, 246, 0.58)
      );
      background-clip: padding-box;
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
    }

    &::-webkit-scrollbar-thumb:hover {
      background: linear-gradient(
        180deg,
        rgba(147, 207, 255, 0.98),
        rgba(59, 130, 246, 0.76)
      );
      background-clip: padding-box;
    }
  }

  @media (max-width: 575.98px) {
    .modal-body {
      scrollbar-gutter: auto;

      &::-webkit-scrollbar {
        width: 9px;
      }
    }
  }
  .modal-footer {
    border-top: 1px solid rgba(255, 255, 255, 0.05);
    padding: 1.5rem 3rem;
    background: rgba(255, 255, 255, 0.02);
  }
`;

const SectionBox = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 1.5rem;
  padding: 2rem;
  margin-bottom: 1.5rem;
  
  h3 {
    font-size: 1.1rem;
    font-weight: 800;
    color: #60a5fa;
    margin-bottom: 1.25rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  
  p, li {
    font-size: 0.95rem;
    color: #cbd5e1;
    line-height: 1.7;
    margin-bottom: 0.75rem;
    word-break: keep-all;
  }
  
  strong {
    color: #fff;
    font-weight: 700;
  }

  ul {
    padding-left: 1.25rem;
    margin-bottom: 0;
    li {
        list-style: none;
        position: relative;
        padding-left: 0.5rem;
        &::before {
            content: '•';
            position: absolute;
            left: -1rem;
            color: #3b82f6;
        }
    }
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  .badge {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    width: fit-content;
    padding: 0.4rem 0.8rem;
    background: rgba(59, 130, 246, 0.1);
    border: 1px solid rgba(59, 130, 246, 0.2);
    border-radius: 999px;
    color: #60a5fa;
    font-size: 0.75rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  h2 {
    font-size: 2rem;
    font-weight: 900;
    color: #fff;
    margin: 0;
  }
`;

const PrivacyModal = ({ show, onHide }) => {
  return (
    <StyledModal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <HeaderTitle>
            <div className="badge"><FiShield /> Data Protection</div>
            <h2>개인정보 처리방침</h2>
        </HeaderTitle>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-5 text-slate-400 font-medium leading-relaxed">
            나니아랩은 고객님의 소중한 정보를 안전하게 보호하기 위해 최선을 다하고 있습니다. <br />
            본 방침은 관련 법령에 의거하여 개인정보가 어떤 용도와 방식으로 이용되고 있는지 안내해 드립니다.
        </p>

        <SectionBox>
            <h3><FiInfo /> 제1조 (개인정보의 목적)</h3>
            <p>회사는 다음의 목적을 위해 필요한 아래의 항목, 최소한의 개인정보를 처리하며, 본 교육참여시 필요한 목적 외의 용도로는 이용하지 않습니다.</p>
            <ul>
                <li>학습지원 서비스 제공 및 기능 운영</li>
                <li>서비스 이용자 식별 및 이용 관리</li>
                <li>학습 과정 기록 관리 및 서비스 품질 개선</li>
                <li>서비스 안정성 확보 및 오류 분석</li>
                <li>이용 문의 대응 및 공지사항 전달</li>
            </ul>
        </SectionBox>

        <SectionBox>
            <h3><FiLock /> 제2조 (처리하는 개인정보의 항목)</h3>
            <ul>
                <li><strong>필수 수집 항목</strong>: 성명, 아이디, 비밀번호, 이메일 주소, 연락처(휴대폰 번호).</li>
                <li><strong>수집 방법</strong>: 공식 홈페이지 회원가입 및 서비스 이용 과정에서의 자발적 입력.</li>
                <li><strong>서비스 회원가입 및 관리</strong>: 회원 가입의사 확인, 학생 및 교사 본인 식별·인증, 회원자격 유지·관리.</li>
                <li><strong>AI 교육 서비스 제공</strong>: 맞춤형 프로젝트 기반 학습 콘텐츠 제공, AI-Helper를 통한 실시간 학습 지원.</li>
                <li><strong>14세 미만 아동의 개인정보 보호</strong>: 만 14세 미만 아동 보호를 위한 법정대리인 동의 확인후 가입이 가능한 형태이며, 14세 미만의 아동은 직접가입이 불가.</li>
            </ul>
            <p>다만, 교육 목적의 학습지원 서비스 특성상 학교 또는 교육기관을 통해 만 14세 미만 아동이 서비스에 참여할 수 있으며, 이 경우 해당 교육기관이 법정대리인의 동의를 적법하게 확보한 것을 전제로 서비스를 제공합니다.</p>
        </SectionBox>

        <SectionBox>
            <h3><FiCheckCircle /> 제3조 (보유 및 이용 기간)</h3>
            <p>회사는 원칙적으로 개인정보 수집 및 이용목적이 달성된 후에는 해당 정보를 지체 없이 파기합니다.</p>
            <ul>
                <li><strong>홈페이지 회원 가입 정보</strong>: 회원 탈퇴 시까지</li>
                <li><strong>관련 법령에 의한 보존</strong>: 계약/청약철회(5년), 소비자 불만/분쟁처리(3년)</li>
            </ul>
        </SectionBox>

        <SectionBox>
            <h3><FiShield /> 제4조 (안전성 확보 조치)</h3>
            <p>회사는 해킹이나 컴퓨터 바이러스 등에 의한 개인정보 유출 및 훼손을 막기 위하여 다음과 같은 기술적/관리적 대책을 강구하고 있습니다.</p>
            <ul>
                <li>개인정보의 암호화 저장 및 전송 구간 보안(SSL) 적용</li>
                <li>접근 권한의 단계적 제한 및 정기적인 내부 보안 감사</li>
            </ul>
        </SectionBox>

        <SectionBox>
            <h3><FiInfo /> 제5조 (정보주체의 권리 및 행사 방법)</h3>
            <p>정보주체는 회사에 대하여 언제든지 자신의 개인정보에 대한 열람, 정정, 삭제 또는 처리정지를 요청할 수 있습니다.</p>
            <ul>
                <li>개인정보 열람 요구</li>
                <li>개인정보 정정 또는 삭제 요구</li>
                <li>개인정보 처리정지 요구</li>
            </ul>
            <p>정보주체의 권리 행사는 회사의 개인정보 보호 담당 부서를 통해 접수할 수 있으며, 회사는 정보주체의 요청이 접수된 경우 관계 법령에 따라 본인 확인 절차를 거쳐 지체 없이 필요한 조치를 취합니다.</p>
        </SectionBox>

        <SectionBox>
            <h3><FiShield /> 제6조 개인정보 보호책임자</h3>
            <ul>
                <li><strong>개인정보 보호책임자</strong>: 김찬석</li>
                <li><strong>문의처</strong>: kcs326hk@timmanage.com</li>
            </ul>
            <p>회사는 개인정보와 관련한 문의 및 불만 사항에 대하여 신속하고 성실하게 답변드리기 위해 노력합니다.</p>
        </SectionBox>

        <p className="text-center text-slate-500 text-sm mt-4">
            최종 업데이트: 2026년 4월 17일 | <strong>나니아랩 (NARNIA LAB)</strong>
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button 
            variant="primary" 
            style={{ 
                borderRadius: '1rem', 
                fontWeight: 800, 
                padding: '0.75rem 2.5rem',
                background: '#3b82f6',
                border: 'none',
                boxShadow: '0 10px 20px rgba(59, 130, 246, 0.3)'
            }} 
            onClick={onHide}
        >
            확인 및 닫기
        </Button>
      </Modal.Footer>
    </StyledModal>
  );
};

export default PrivacyModal;
